import { useCallback } from "react";
import { StringParam, useQueryParam } from "use-query-params";

export const VIDEO_EDITOR_PARAM = "editVideo";

// The open editor lives in the URL (`?editVideo=<slot>`) so a refresh or a
// shared link reopens it. `slot` names which video on the page it edits; a
// page can hold several (one per course screen or skill step).
//
// Opening and closing replace the history entry rather than push one: Back
// would otherwise close the editor without its save-before-close flow.
export const useVideoEditorParam = (
  slot: string | null | undefined,
): [boolean, (open: boolean) => void] => {
  const [value, setValue] = useQueryParam(VIDEO_EDITOR_PARAM, StringParam);
  const open = !!slot && value === slot;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!slot) return;
      setValue(next ? slot : undefined, "replaceIn");
    },
    [setValue, slot],
  );
  return [open, setOpen];
};

// A path without the open editor, for code that records a URL and restores
// it later (builder undo/redo): restoring `editVideo` would reopen the editor.
export const withoutVideoEditorParam = (path: string): string => {
  const hashAt = path.indexOf("#");
  const beforeHash = hashAt < 0 ? path : path.slice(0, hashAt);
  const hash = hashAt < 0 ? "" : path.slice(hashAt);
  const queryAt = beforeHash.indexOf("?");
  if (queryAt < 0) return path;
  const params = new URLSearchParams(beforeHash.slice(queryAt + 1));
  if (!params.has(VIDEO_EDITOR_PARAM)) return path;
  params.delete(VIDEO_EDITOR_PARAM);
  const query = params.toString();
  return `${beforeHash.slice(0, queryAt)}${query ? `?${query}` : ""}${hash}`;
};
